/**
 * VideoWalkRecorder — the mobile-first capture surface for a Video Walk.
 *
 * User-facing controls: Start · Pause · Resume · Finish · Cancel.
 * Segments are silently managed by useVideoWalkSession.
 *
 * The audio-only recovery path activates transparently when camera access
 * is denied, and is presented as a subtle hint — NOT as a third primary
 * capture mode.
 */
import { useCallback, useRef, useState } from "react";
import {
  Video, Mic, Pause, Play, Square, Upload, AlertTriangle, Loader2, X,
} from "lucide-react";
import { useVideoWalkSession } from "@/hooks/useVideoWalkSession";
import VideoWalkSummary from "./VideoWalkSummary";

const fmt = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

export default function VideoWalkRecorder({ projectId, leviEstimateSessionId, onFinalized, embedded = false, primaryCta }) {
  const [finalizedSession, setFinalizedSession] = useState(null);
  const fileInputRef = useRef(null);

  const {
    session, status, kind, elapsedMs, cameraAvailable, error, uploading,
    startWalk, pauseWalk, resumeWalk, finishWalk, cancelWalk, saveSegmentAndContinue,
    uploadExistingFile, bindPreview,
  } = useVideoWalkSession({ projectId, leviEstimateSessionId });

  const onFinish = useCallback(async () => {
    const finalized = await finishWalk();
    if (finalized) {
      setFinalizedSession(finalized);
      if (onFinalized) onFinalized(finalized);
    }
  }, [finishWalk, onFinalized]);

  const onCancel = useCallback(async () => {
    await cancelWalk();
    setFinalizedSession(null);
  }, [cancelWalk]);

  if (finalizedSession) {
    return (
      <VideoWalkSummary
        session={finalizedSession}
        onStartAnother={embedded ? null : () => setFinalizedSession(null)}
        primaryCta={primaryCta}
      />
    );
  }

  // Field-Mode (PO 2026-08-08 mobile QA, refined 2026-08-09).
  // While actively capturing on mobile, the live camera dominates the
  // viewport and the three intent controls sit in a fixed bottom
  // action area that respects iPhone safe-area inset. Normal mobile
  // JobClose bottom navigation is hidden while Field Mode is active
  // (see `data-field-mode="1"` — Shell/DashboardLayout listens for
  // this attribute on the recorder and suppresses its own bottom
  // nav on the mobile breakpoint). Desktop keeps the normal inline
  // layout.
  const isFieldActive = ["recording", "paused", "finalizing"].includes(status);
  const outerClass = isFieldActive
    ? "fixed inset-0 z-50 bg-black text-white flex flex-col overflow-hidden md:relative md:inset-auto md:z-auto md:bg-transparent md:text-inherit md:overflow-visible " +
      (embedded ? "md:p-0 md:space-y-4" : "md:rounded-xl md:border md:border-[var(--jc-border)] md:bg-[var(--jc-bg)] md:p-4 md:space-y-4")
    : (embedded
        ? "space-y-4"
        : "rounded-xl border border-[var(--jc-border)] bg-[var(--jc-bg)] p-4 space-y-4");

  return (
    <div
      className={outerClass}
      data-testid="video-walk-recorder"
      data-field-mode={isFieldActive ? "1" : "0"}
    >
      {!embedded && !isFieldActive && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold" data-testid="video-walk-headline">
              Record a Video Job Walk
            </div>
            <p className="text-sm text-[var(--jc-text-soft)] mt-1 max-w-xl">
              Record the job. Describe what you see. Levi organizes the scope.
            </p>
          </div>
          {status !== "idle" && (
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-[var(--jc-text-soft)] hover:text-red-600 inline-flex items-center gap-1"
              data-testid="video-walk-cancel-btn"
            >
              <X size={14} /> Cancel
            </button>
          )}
        </div>
      )}
      {embedded && status !== "idle" && !isFieldActive && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-[var(--jc-text-soft)] hover:text-red-600 inline-flex items-center gap-1"
            data-testid="video-walk-cancel-btn"
          >
            <X size={14} /> Cancel
          </button>
        </div>
      )}
      {/* Field-Mode top bar (mobile only) — Cancel as small text link
          in the top-right so it never competes with the primary
          controls at the bottom of the screen. */}
      {isFieldActive && (
        <div
          className="md:hidden flex items-center justify-end px-3 pt-3"
          style={{ paddingTop: "max(env(safe-area-inset-top), 0.75rem)" }}
        >
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-white/80 hover:text-white inline-flex items-center gap-1 bg-black/40 rounded-full px-3 py-1"
            data-testid="video-walk-cancel-btn"
          >
            <X size={14} /> Cancel
          </button>
        </div>
      )}

      {/* Live preview — Field Mode (mobile) fills the field surface
          minus the fixed bottom action bar; desktop and idle states
          keep the compact aspect ratio. */}
      <div className={
        "relative overflow-hidden bg-[var(--jc-surface)] border border-[var(--jc-border)] " +
        (isFieldActive
          ? "flex-1 min-h-0 mx-3 rounded-lg md:min-h-0 md:aspect-video md:flex-none md:mx-0"
          : "aspect-video rounded-lg")
      }>
        <video
          ref={bindPreview}
          autoPlay
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="video-walk-preview"
        />
        {status === "idle" && (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--jc-text-soft)] text-sm" data-testid="video-walk-preview-placeholder">
            <Video size={40} strokeWidth={1.25} />
          </div>
        )}
        {(status === "recording" || status === "paused") && (
          <div className="absolute top-3 left-3 flex items-center gap-2 text-white bg-black/60 rounded-full px-3 py-1 text-xs font-mono">
            <span
              className={`inline-block w-2 h-2 rounded-full ${status === "recording" ? "bg-red-500 animate-pulse" : "bg-yellow-400"}`}
            />
            <span data-testid="video-walk-elapsed">{fmt(elapsedMs)}</span>
          </div>
        )}
        {kind === "audio_fallback" && (
          <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs rounded-full px-3 py-1 inline-flex items-center gap-1">
            <Mic size={12} /> Camera unavailable — recording audio only
          </div>
        )}
        {uploading && (
          <div className="absolute bottom-3 right-3 text-white text-xs bg-black/60 rounded-full px-3 py-1 inline-flex items-center gap-1">
            <Loader2 size={12} className="animate-spin" /> Uploading
          </div>
        )}
      </div>

      {/* Controls */}
      {/* Field-Mode bottom action bar (mobile only). Stacked full-width
          buttons in unambiguous vertical order. Respects iPhone
          safe-area inset so home-indicator/browser chrome never
          overlaps Finish Walkthrough. Each control carries a
          one-line hint below its label. */}
      {isFieldActive && (status === "recording" || status === "paused") && (
        <div
          className="md:hidden w-full bg-black/95 border-t border-white/10 px-4 pt-3 flex flex-col gap-2"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
          data-testid="video-walk-field-actions"
        >
          {status === "recording" ? (
            <button
              type="button"
              onClick={pauseWalk}
              className="w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-md bg-white/10 text-white px-4 py-2.5 text-sm font-semibold hover:bg-white/15"
              data-testid="video-walk-pause-btn"
            >
              <span className="inline-flex items-center gap-2"><Pause size={16} /> Pause</span>
              <span className="text-[11px] font-normal opacity-70">Pause recording · you can resume</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={resumeWalk}
              className="w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-md bg-red-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-red-700"
              data-testid="video-walk-resume-btn"
            >
              <span className="inline-flex items-center gap-2"><Play size={16} /> Resume</span>
              <span className="text-[11px] font-normal opacity-80">Continue recording where you left off</span>
            </button>
          )}
          <button
            type="button"
            onClick={saveSegmentAndContinue}
            className="w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-md border-2 border-blue-400 text-white bg-blue-500/20 px-4 py-2.5 text-sm font-semibold hover:bg-blue-500/30"
            data-testid="video-walk-add-segment-btn"
          >
            <span className="inline-flex items-center gap-2"><Upload size={16} /> Add Another Segment</span>
            <span className="text-[11px] font-normal opacity-80">Save clip · keep walking the job</span>
          </button>
          <button
            type="button"
            onClick={onFinish}
            className="w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-md bg-white text-black px-4 py-3 text-sm font-semibold shadow-sm hover:bg-white/90"
            data-testid="video-walk-finish-btn"
          >
            <span className="inline-flex items-center gap-2"><Square size={16} /> Finish Walkthrough</span>
            <span className="text-[11px] font-normal opacity-70">I'm done showing Levi the job</span>
          </button>
        </div>
      )}
      {/* Desktop + non-field-active controls preserved below. */}
      <div className={
        "space-y-2 " + (isFieldActive ? "hidden md:block" : "")
      } data-testid="video-walk-controls">
        {status === "idle" && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={startWalk}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-red-700 shadow-sm"
                data-testid="video-walk-start-btn"
              >
                <Video size={16} /> {session?.segments?.length ? "Record Next Segment" : "Start Video Job Walk"}
              </button>
              {/* Stitch 4 · If the walk already has segments (contractor
                  used Add Another Segment), keep Finish Walkthrough
                  reachable from the idle state so the contractor can
                  end the walkthrough between segments. */}
              {session?.segments?.length > 0 && (
                <button
                  type="button"
                  onClick={onFinish}
                  className="inline-flex items-center gap-2 rounded-md bg-black text-white px-4 py-2.5 text-sm font-semibold"
                  data-testid="video-walk-finish-btn-idle"
                >
                  <Square size={16} /> Finish Walkthrough ({session.segments.length})
                </button>
              )}
            </div>
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] underline inline-flex items-center gap-1"
                data-testid="video-walk-upload-existing-btn"
              >
                <Upload size={12} /> Or upload an existing narrated video
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,audio/mpeg,audio/mp4,audio/wav,audio/webm,audio/ogg"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadExistingFile(f);
                e.target.value = "";
              }}
              data-testid="video-walk-upload-existing-input"
            />
          </>
        )}
        {status === "recording" && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={pauseWalk}
              className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm"
              data-testid="video-walk-pause-btn-desktop"
            >
              <Pause size={16} /> Pause
            </button>
            <button
              type="button"
              onClick={saveSegmentAndContinue}
              className="inline-flex items-center gap-2 rounded-md border-2 border-[var(--jc-blue)] text-[var(--jc-blue-700)] bg-white px-4 py-2 text-sm font-semibold hover:bg-[#EFF6FF]"
              data-testid="video-walk-add-segment-btn-desktop"
              title="Save this clip and continue walking. Levi will keep everything together for the final wrap-up."
            >
              <Upload size={16} /> Add Another Segment
            </button>
            <button
              type="button"
              onClick={onFinish}
              className="inline-flex items-center gap-2 rounded-md bg-black text-white px-4 py-2 text-sm font-semibold"
              data-testid="video-walk-finish-btn-desktop"
            >
              <Square size={16} /> Finish Walkthrough
            </button>
          </div>
        )}
        {status === "paused" && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={resumeWalk}
              className="inline-flex items-center gap-2 rounded-md bg-red-600 text-white px-4 py-2 text-sm font-semibold"
              data-testid="video-walk-resume-btn-desktop"
            >
              <Play size={16} /> Resume
            </button>
            <button
              type="button"
              onClick={saveSegmentAndContinue}
              className="inline-flex items-center gap-2 rounded-md border-2 border-[var(--jc-blue)] text-[var(--jc-blue-700)] bg-white px-4 py-2 text-sm font-semibold hover:bg-[#EFF6FF]"
              data-testid="video-walk-add-segment-btn-desktop-paused"
            >
              <Upload size={16} /> Add Another Segment
            </button>
            <button
              type="button"
              onClick={onFinish}
              className="inline-flex items-center gap-2 rounded-md bg-black text-white px-4 py-2 text-sm font-semibold"
              data-testid="video-walk-finish-btn-desktop-paused"
            >
              <Square size={16} /> Finish Walkthrough
            </button>
          </div>
        )}
        {status === "requesting" && (
          <div className="text-sm text-[var(--jc-text-soft)] inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Requesting camera & microphone…
          </div>
        )}
        {status === "finalizing" && (
          <div className="text-sm text-[var(--jc-text-soft)] inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Levi is organizing your Video Job Walk…
          </div>
        )}
      </div>

      {error && (
        <div
          className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm p-3 inline-flex items-start gap-2"
          data-testid="video-walk-error"
        >
          <AlertTriangle size={16} className="mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {!cameraAvailable && status === "idle" && (
        <div className="text-xs text-[var(--jc-text-soft)]">
          Camera unavailable in this browser — the recorder will fall back to audio-only automatically.
        </div>
      )}
    </div>
  );
}
